// src/pages/Customers/CustomerPricing.jsx

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, collection, getDoc, getDocs, deleteDoc, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import useCustomerPricing from '../../hooks/useCustomerPricing';

const styles = {
    container: { 
        fontFamily: '"Inter", sans-serif',
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px'
    },
    title: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#1e293b'
    },
    backButton: {
        textDecoration: 'none',
        color: '#3b82f6',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    box: {
        background: 'white',
        padding: '30px',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        marginBottom: '20px'
    },
    formGroup: {
        marginBottom: '20px'
    },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '600',
        color: '#374151'
    },
    input: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '16px',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        boxSizing: 'border-box'
    },
    select: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '16px',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        background: 'white'
    },
    button: {
        padding: '12px 24px',
        border: 'none',
        background: '#3b82f6',
        color: 'white',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '600'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        marginTop: '20px'
    },
    th: {
        background: '#f9fafb',
        padding: '16px',
        textAlign: 'left',
        borderBottom: '2px solid #e5e7eb',
        fontWeight: '600',
        color: '#374151'
    },
    td: {
        padding: '16px',
        borderBottom: '1px solid #e5e7eb',
        verticalAlign: 'middle'
    },
    deleteButton: {
        background: 'none',
        border: 'none',
        color: '#ef4444',
        cursor: 'pointer',
        fontSize: '20px'
    },
    emptyState: {
        textAlign: 'center',
        padding: '40px',
        color: '#6b7280'
    },
    productImage: {
        width: '50px',
        height: '50px',
        objectFit: 'cover',
        borderRadius: '6px',
        border: '1px solid #e5e7eb'
    },
    tabContainer: {
        display: 'flex',
        borderBottom: '2px solid #e5e7eb',
        marginBottom: '24px',
        gap: '0'
    },
    tab: {
        padding: '14px 28px',
        cursor: 'pointer',
        borderBottom: '3px solid transparent',
        color: '#64748b',
        fontWeight: '600',
        background: 'none',
        border: 'none',
        fontSize: '15px',
        transition: 'all 0.2s',
        marginBottom: '-2px'
    },
    tabActive: {
        borderBottom: '3px solid #06C167',
        color: '#06C167'
    },
    badge: {
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600'
    }
};

function CustomerPricing() {
    const { customerId } = useParams();
    const { userData } = useAuth();
    const { 
        customerPrices, saveCustomerPrice, 
        customerGroupPrices, saveGroupPrice, deleteGroupPrice,
        customerGroups
    } = useCustomerPricing(userData?.companyId);
    
    const [customer, setCustomer] = useState(null);
    const [products, setProducts] = useState([]);
    const [productGroups, setProductGroups] = useState([]);
    const [customerProductPrices, setCustomerProductPrices] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState('');
    const [customPrice, setCustomPrice] = useState('');
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('product'); // 'product' or 'group'

    // Group pricing form state
    const [selectedProductGroup, setSelectedProductGroup] = useState('');
    const [discountType, setDiscountType] = useState('percentage');
    const [discountValue, setDiscountValue] = useState('');

    useEffect(() => {
        if (!userData?.companyId || !customerId) return;

        const loadData = async () => {
            try {
                setLoading(true);
                
                // Load customer data
                const customerRef = doc(db, 'companies', userData.companyId, 'customers', customerId);
                const customerSnap = await getDoc(customerRef);
                if (customerSnap.exists()) {
                    setCustomer({ id: customerSnap.id, ...customerSnap.data() });
                }
                
                // Load products
                const productsRef = collection(db, 'companies', userData.companyId, 'products');
                const productsSnap = await getDocs(productsRef);
                const productsData = productsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setProducts(productsData);

                // Load product groups
                const groupsRef = collection(db, 'companies', userData.companyId, 'productGroups');
                const groupsSnap = await getDocs(groupsRef);
                setProductGroups(groupsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                
                // Load customer-specific prices
                const pricesRef = collection(db, 'companies', userData.companyId, 'customerPrices');
                const q = query(pricesRef, where('customerId', '==', customerId));
                const unsubscribe = onSnapshot(q, (snapshot) => {
                    const prices = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    setCustomerProductPrices(prices);
                });
                
                return () => unsubscribe();
            } catch (error) {
                console.error('Error loading data:', error);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [userData, customerId]);

    // ---- Product-level pricing handlers ----
    const handleAddPrice = async () => {
        if (!selectedProduct || !customPrice) {
            alert('Lütfen bir ürün ve fiyat seçin.');
            return;
        }

        try {
            await saveCustomerPrice(customerId, selectedProduct, parseFloat(customPrice));
            setSelectedProduct('');
            setCustomPrice('');
        } catch (error) {
            console.error('Error saving price:', error);
            alert('Fiyat kaydedilirken bir hata oluştu.');
        }
    };

    const handleDeletePrice = async (priceId) => {
        if (!window.confirm('Bu özel fiyatı silmek istediğinizden emin misiniz?')) {
            return;
        }

        try {
            const priceRef = doc(db, 'companies', userData.companyId, 'customerPrices', priceId);
            await deleteDoc(priceRef);
        } catch (error) {
            console.error('Error deleting price:', error);
            alert('Fiyat silinirken bir hata oluştu.');
        }
    };

    // ---- Group+Category pricing handlers ----
    const handleAddGroupPrice = async () => {
        if (!selectedProductGroup || !discountValue) {
            alert('Lütfen bir ürün grubu ve indirim değeri seçin.');
            return;
        }
        if (!customer?.customerGroupId) {
            alert('Bu müşterinin bir müşteri grubu tanımlı değil. Lütfen önce müşteri bilgilerinden bir grup atayın.');
            return;
        }

        const productGroup = productGroups.find(g => g.id === selectedProductGroup);
        if (!productGroup) return;

        try {
            await saveGroupPrice(
                customer.customerGroupId,
                customer.customerGroupName,
                selectedProductGroup,
                productGroup.name,
                discountType,
                parseFloat(discountValue)
            );
            setSelectedProductGroup('');
            setDiscountValue('');
            setDiscountType('percentage');
        } catch (error) {
            console.error('Error saving group price:', error);
            alert('Grup fiyatı kaydedilirken bir hata oluştu.');
        }
    };

    const handleDeleteGroupPrice = async (groupPriceId) => {
        if (!window.confirm('Bu grup fiyatını silmek istediğinizden emin misiniz?')) return;
        try {
            await deleteGroupPrice(groupPriceId);
        } catch (error) {
            console.error('Error deleting group price:', error);
            alert('Grup fiyatı silinirken bir hata oluştu.');
        }
    };

    const getProductById = (productId) => {
        return products.find(p => p.id === productId);
    };

    // Filter group prices for this customer's group
    const customerGroupPricesList = customer?.customerGroupId 
        ? customerGroupPrices.filter(gp => gp.customerGroupId === customer.customerGroupId)
        : [];

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.box}>
                    <p>Yükleniyor...</p>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>
                        {customer ? `${customer.name} - Özel Fiyatlar` : 'Müşteri Bulunamadı'}
                    </h1>
                    {customer?.customerGroupName && (
                        <div style={{ marginTop: '8px' }}>
                            <span style={{ ...styles.badge, background: '#e0e7ff', color: '#4338ca' }}>
                                Grup: {customer.customerGroupName}
                            </span>
                        </div>
                    )}
                </div>
                <Link to={`/musteriler/${customerId}`} style={styles.backButton}>
                    &larr; Müşteri Detayına Dön
                </Link>
            </div>

            {customer && (
                <>
                    {/* Tab Navigation */}
                    <div style={styles.tabContainer}>
                        <button
                            style={{ ...styles.tab, ...(activeTab === 'product' ? styles.tabActive : {}) }}
                            onClick={() => setActiveTab('product')}
                        >
                            📦 Ürün Bazlı Fiyatlar
                        </button>
                        <button
                            style={{ ...styles.tab, ...(activeTab === 'group' ? styles.tabActive : {}) }}
                            onClick={() => setActiveTab('group')}
                        >
                            🏷️ Grup + Kategori Fiyatları
                        </button>
                    </div>

                    {/* ====== Product-Level Pricing Tab ====== */}
                    {activeTab === 'product' && (
                        <>
                            <div style={styles.box}>
                                <h3>Yeni Özel Fiyat Ekle</h3>
                                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '20px', alignItems: 'end' }}>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Ürün</label>
                                        <select 
                                            style={styles.select} 
                                            value={selectedProduct} 
                                            onChange={(e) => setSelectedProduct(e.target.value)}
                                        >
                                            <option value="">Ürün Seçin</option>
                                            {products.map(product => (
                                                <option key={product.id} value={product.id}>
                                                    {product.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Özel Fiyat</label>
                                        <input 
                                            type="number" 
                                            step="0.01"
                                            style={styles.input} 
                                            value={customPrice} 
                                            onChange={(e) => setCustomPrice(e.target.value)}
                                            placeholder="Fiyat girin"
                                        />
                                    </div>
                                    
                                    <button 
                                        style={styles.button} 
                                        onClick={handleAddPrice}
                                    >
                                        Ekle
                                    </button>
                                </div>
                            </div>

                            <div style={styles.box}>
                                <h3>Mevcut Özel Fiyatlar</h3>
                                {customerProductPrices.length === 0 ? (
                                    <div style={styles.emptyState}>
                                        <p>Bu müşteri için henüz özel fiyat tanımlanmamış.</p>
                                    </div>
                                ) : (
                                    <table style={styles.table}>
                                        <thead>
                                            <tr>
                                                <th style={styles.th}>Ürün</th>
                                                <th style={styles.th}>Özel Fiyat</th>
                                                <th style={styles.th}>İşlemler</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {customerProductPrices.map(price => {
                                                const product = getProductById(price.productId);
                                                return (
                                                    <tr key={price.id}>
                                                        <td style={styles.td}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                                {product?.imageUrl ? (
                                                                    <img 
                                                                        src={product.imageUrl} 
                                                                        alt={product.name}
                                                                        style={styles.productImage}
                                                                        onError={(e) => {
                                                                            e.target.style.display = 'none';
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div style={{
                                                                        ...styles.productImage,
                                                                        background: '#f3f4f6',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        color: '#6b7280'
                                                                    }}>
                                                                        📦
                                                                    </div>
                                                                )}
                                                                <span>{product?.name || 'Ürün bulunamadı'}</span>
                                                            </div>
                                                        </td>
                                                        <td style={styles.td}>
                                                            {price.price.toFixed(2)} {customer.currency === 'USD' ? '$' : '₺'}
                                                        </td>
                                                        <td style={styles.td}>
                                                            <button 
                                                                style={styles.deleteButton} 
                                                                onClick={() => handleDeletePrice(price.id)}
                                                            >
                                                                &times;
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </>
                    )}

                    {/* ====== Group + Category Pricing Tab ====== */}
                    {activeTab === 'group' && (
                        <>
                            {!customer.customerGroupId ? (
                                <div style={styles.box}>
                                    <div style={styles.emptyState}>
                                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                                        <h3 style={{ color: '#d97706', marginBottom: '8px' }}>Müşteri Grubu Tanımlı Değil</h3>
                                        <p style={{ maxWidth: '400px', margin: '0 auto' }}>
                                            Grup bazlı fiyatlandırma yapabilmek için önce bu müşteriye bir grup atanmalıdır. 
                                            Müşteri detay sayfasında düzenleme yaparak grup atayabilirsiniz.
                                        </p>
                                        <Link 
                                            to={`/musteriler/${customerId}`} 
                                            style={{ ...styles.button, display: 'inline-block', marginTop: '16px', textDecoration: 'none' }}
                                        >
                                            Müşteri Bilgilerini Düzenle
                                        </Link>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div style={styles.box}>
                                        <h3>Yeni Grup Fiyatlandırma Ekle</h3>
                                        <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
                                            <strong>{customer.customerGroupName}</strong> grubundaki tüm müşteriler için ürün kategorisi bazında indirim tanımlayın.
                                        </p>
                                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '16px', alignItems: 'end' }}>
                                            <div style={styles.formGroup}>
                                                <label style={styles.label}>Ürün Grubu (Kategori)</label>
                                                <select 
                                                    style={styles.select} 
                                                    value={selectedProductGroup} 
                                                    onChange={(e) => setSelectedProductGroup(e.target.value)}
                                                >
                                                    <option value="">Kategori Seçin</option>
                                                    {productGroups.map(group => (
                                                        <option key={group.id} value={group.id}>
                                                            {group.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            
                                            <div style={styles.formGroup}>
                                                <label style={styles.label}>İndirim Tipi</label>
                                                <select 
                                                    style={styles.select} 
                                                    value={discountType} 
                                                    onChange={(e) => setDiscountType(e.target.value)}
                                                >
                                                    <option value="percentage">% Yüzde İndirim</option>
                                                    <option value="fixed">Sabit Fiyat</option>
                                                </select>
                                            </div>

                                            <div style={styles.formGroup}>
                                                <label style={styles.label}>
                                                    {discountType === 'percentage' ? 'İndirim Oranı (%)' : 'Sabit Fiyat (₺)'}
                                                </label>
                                                <input 
                                                    type="number" 
                                                    step="0.01"
                                                    min="0"
                                                    max={discountType === 'percentage' ? '100' : undefined}
                                                    style={styles.input} 
                                                    value={discountValue} 
                                                    onChange={(e) => setDiscountValue(e.target.value)}
                                                    placeholder={discountType === 'percentage' ? 'Örn: 10' : 'Örn: 150.00'}
                                                />
                                            </div>
                                            
                                            <button 
                                                style={{ ...styles.button, background: '#06C167' }} 
                                                onClick={handleAddGroupPrice}
                                            >
                                                Ekle
                                            </button>
                                        </div>
                                    </div>

                                    <div style={styles.box}>
                                        <h3>
                                            Mevcut Grup Fiyatlandırmaları
                                            <span style={{ fontSize: '14px', fontWeight: '400', color: '#64748b', marginLeft: '12px' }}>
                                                ({customer.customerGroupName} grubu)
                                            </span>
                                        </h3>
                                        {customerGroupPricesList.length === 0 ? (
                                            <div style={styles.emptyState}>
                                                <p>Bu grup için henüz kategori bazlı fiyatlandırma tanımlanmamış.</p>
                                            </div>
                                        ) : (
                                            <table style={styles.table}>
                                                <thead>
                                                    <tr>
                                                        <th style={styles.th}>Ürün Grubu (Kategori)</th>
                                                        <th style={styles.th}>İndirim Tipi</th>
                                                        <th style={styles.th}>Değer</th>
                                                        <th style={styles.th}>İşlemler</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {customerGroupPricesList.map(gp => (
                                                        <tr key={gp.id}>
                                                            <td style={styles.td}>
                                                                <span style={{ ...styles.badge, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>
                                                                    {gp.productGroupName}
                                                                </span>
                                                            </td>
                                                            <td style={styles.td}>
                                                                {gp.discountType === 'percentage' ? (
                                                                    <span style={{ ...styles.badge, background: '#eff6ff', color: '#1d4ed8' }}>
                                                                        % Yüzde İndirim
                                                                    </span>
                                                                ) : (
                                                                    <span style={{ ...styles.badge, background: '#fef3c7', color: '#92400e' }}>
                                                                        Sabit Fiyat
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td style={{ ...styles.td, fontWeight: '700', color: '#059669', fontSize: '16px' }}>
                                                                {gp.discountType === 'percentage' 
                                                                    ? `%${gp.discountValue}` 
                                                                    : `${gp.discountValue.toFixed(2)} ${customer.currency === 'USD' ? '$' : '₺'}`
                                                                }
                                                            </td>
                                                            <td style={styles.td}>
                                                                <button 
                                                                    style={styles.deleteButton} 
                                                                    onClick={() => handleDeleteGroupPrice(gp.id)}
                                                                >
                                                                    &times;
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>

                                    {/* Preview: affected products */}
                                    {customerGroupPricesList.length > 0 && (
                                        <div style={styles.box}>
                                            <h3>📋 Etkilenen Ürünler Önizleme</h3>
                                            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                                                Grup fiyatlandırmasından etkilenen ürünler ve hesaplanan fiyatları:
                                            </p>
                                            <table style={styles.table}>
                                                <thead>
                                                    <tr>
                                                        <th style={styles.th}>Ürün</th>
                                                        <th style={styles.th}>Kategori</th>
                                                        <th style={styles.th}>Normal Fiyat</th>
                                                        <th style={styles.th}>İndirimli Fiyat</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {products
                                                        .filter(p => customerGroupPricesList.some(gp => gp.productGroupId === p.groupId))
                                                        .slice(0, 20)
                                                        .map(product => {
                                                            const gp = customerGroupPricesList.find(g => g.productGroupId === product.groupId);
                                                            const originalPrice = product.salePrice || 0;
                                                            const discountedPrice = gp?.discountType === 'percentage' 
                                                                ? originalPrice * (1 - gp.discountValue / 100) 
                                                                : gp?.discountValue || originalPrice;
                                                            const groupName = productGroups.find(pg => pg.id === product.groupId)?.name || '-';
                                                            
                                                            // Check if there's an individual price override
                                                            const hasIndividualPrice = customerProductPrices.some(cp => cp.productId === product.id);

                                                            return (
                                                                <tr key={product.id} style={hasIndividualPrice ? { opacity: 0.5 } : {}}>
                                                                    <td style={styles.td}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                            {product.imageUrl ? (
                                                                                <img src={product.imageUrl} alt="" style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover' }} onError={e => e.target.style.display='none'} />
                                                                            ) : (
                                                                                <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>📦</div>
                                                                            )}
                                                                            <div>
                                                                                <div style={{ fontWeight: '500' }}>{product.name}</div>
                                                                                {hasIndividualPrice && (
                                                                                    <div style={{ fontSize: '11px', color: '#d97706' }}>⚠ Ürün bazlı fiyat tanımlı (öncelikli)</div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td style={styles.td}>
                                                                        <span style={{ ...styles.badge, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>
                                                                            {groupName}
                                                                        </span>
                                                                    </td>
                                                                    <td style={{ ...styles.td, textDecoration: !hasIndividualPrice ? 'line-through' : 'none', color: '#94a3b8' }}>
                                                                        {originalPrice.toFixed(2)} {customer.currency === 'USD' ? '$' : '₺'}
                                                                    </td>
                                                                    <td style={{ ...styles.td, fontWeight: '700', color: '#059669' }}>
                                                                        {Math.round(discountedPrice * 100 / 100).toFixed(2)} {customer.currency === 'USD' ? '$' : '₺'}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    }
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
}

export default CustomerPricing;